import { createSlice } from '@reduxjs/toolkit';
import { checkEmailExists } from './thunks/checkEmailExists.js';
import { getWaitlistGoal } from './thunks/getWaitlistGoal.js';
import { submitWaitlistEntry } from './thunks/submitWaitlistEntry.js';
import { getWaitlistStats } from './thunks/getWaitlistStats.js';
import { checkUserApproval } from './thunks/checkUserApproval.js';

const initialState = {
  // Submission state
  loading: false,
  error: null,
  isSubmitted: false,
  submittedEntry: null,

  // Waitlist stats
  waitlistCount: null,
  stats: {
    totalCount: 0,
    approvedCount: 0,
    pendingCount: 0
  },
  lastStatsUpdate: null,

  // Goal settings
  goal: 20000,
  goalLastUpdate: null,

  // User approval status
  isUserApproved: null,
  checkingApproval: false,

  // Email check
  emailExists: false,
  checkingEmail: false
};

const waitlistSlice = createSlice({
  name: 'waitlist',
  initialState,
  reducers: {
    // Reset submission state
    resetSubmission: (state) => {
      state.isSubmitted = false;
      state.submittedEntry = null;
      state.error = null;
    },

    // Clear errors
    clearError: (state) => {
      state.error = null;
    },

    // Reset approval check
    resetApprovalCheck: (state) => {
      state.isUserApproved = null;
      state.checkingApproval = false;
    },

    // Phase 5 Fix: Reset approval state when auth user changes
    resetApprovalState: (state) => {
      state.isUserApproved = null;
      state.checkingApproval = false;
    }
  },
  extraReducers: (builder) => {
    // Submit waitlist entry
    builder.addCase(submitWaitlistEntry.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(submitWaitlistEntry.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.isSubmitted = true;
      state.submittedEntry = payload;
      state.waitlistCount = payload.waitlistCount;
    });
    builder.addCase(submitWaitlistEntry.rejected, (state, { payload }) => {
      state.loading = false;
      state.error = payload;
    });

    // Check email exists
    builder.addCase(checkEmailExists.pending, (state) => {
      state.checkingEmail = true;
    });
    builder.addCase(checkEmailExists.fulfilled, (state, { payload }) => {
      state.checkingEmail = false;
      state.emailExists = payload;
    });
    builder.addCase(checkEmailExists.rejected, (state) => {
      state.checkingEmail = false;
    });

    // Get waitlist stats
    builder.addCase(getWaitlistStats.pending, (state) => {
      // Keep loading separate for stats
    });
    builder.addCase(getWaitlistStats.fulfilled, (state, { payload }) => {
      state.stats = payload;
      state.waitlistCount = payload.totalCount;
      state.lastStatsUpdate = payload.lastUpdated || Date.now();
    });
    builder.addCase(getWaitlistStats.rejected, (state) => {
      // Silently fail stats loading
    });

    // Get waitlist goal
    builder.addCase(getWaitlistGoal.fulfilled, (state, { payload }) => {
      state.goal = payload.goal;
      state.goalLastUpdate = payload.lastUpdated || Date.now();
    });

    // Check user approval
    builder.addCase(checkUserApproval.pending, (state) => {
      state.checkingApproval = true;
    });
    builder.addCase(checkUserApproval.fulfilled, (state, { payload }) => {
      state.checkingApproval = false;
      state.isUserApproved = payload;
    });
    builder.addCase(checkUserApproval.rejected, (state, { payload }) => {
      state.checkingApproval = false;
      state.error = payload;
    });
  }
});

export const { resetSubmission, clearError, resetApprovalCheck, resetApprovalState } = waitlistSlice.actions;
export default waitlistSlice.reducer;
